import React, { useEffect, useState } from "react";
import {
  Badge,
  Button,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import ModalBase from "./ModalBase";
import {
  asyncDelete,
  asyncGetList,
  asyncPostData,
  uploadFile,
} from "../../../utils/httpRequests";
import { useSelector } from "react-redux";
import { cloneDeep, isArray } from "lodash";
import {
  downloadFile,
  formatDateDisplay,
  generateLinkImage,
} from "../../../utils/helpers";
import { MdAttachFile, MdSend } from "react-icons/md";
import { useAlertContext } from "../../../contexts/alert/AlertProvider";
import { LuTrash, LuXCircle } from "react-icons/lu";
import { v4 } from "uuid";
import { FiDownload } from "react-icons/fi";

interface IModalReplyComplaint {
  data: any;
}
const LIMIT = 20;

function ModalReplyComplaint({ data }: IModalReplyComplaint) {
  const { token } = useSelector((state: any) => state.auth);
  const { showAlert } = useAlertContext();
  const [open, setOpen] = useState<boolean>(false);
  const [messages, setMessages] = useState<any[]>([]);
  const [moreMessages, setMoreMessages] = useState<any[]>([]);
  const [text, setText] = useState<string>("");
  const [files, setFiles] = useState<File[]>([]);

  const reset = () => {
    setText("");
    setFiles([]);
  };

  const handleClose = () => {
    setOpen(false);
    setMessages([]);
    setMoreMessages([]);
    reset();
  };

  const deleteFile = (index: number) => {
    const clonedFiles = cloneDeep(files);
    clonedFiles.splice(index, 1);
    setFiles(clonedFiles);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files as FileList);
    setFiles(files);
  };

  const sendMessage = async () => {
    const content = text.trim();
    if (!content && !(isArray(files) && files?.length > 0)) return;
    const dataToPost: { [key: string]: any } = {
      parent: data?._id,
      content,
      exfields: {
        admin: true,
      },
      attends: [],
    };
    // save images
    if (isArray(files) && files.length > 0) {
      for (let i = 0; i < files.length; i++) {
        const formData = new FormData();
        formData.append("file", files[i]);
        const resp = await uploadFile({
          formData,
          folder: "comment2",
          token,
        });
        dataToPost.attends.push({
          ...resp?.data,
          url: generateLinkImage(resp?.data?.fileUrl),
        });
      }
    }
    const resp = await asyncPostData({
      apiCode: "comment2",
      token,
      data: dataToPost,
    });
    if (resp && resp.status === 200) {
      setMoreMessages((prev) => [...prev, resp.data]);
      reset();
    } else {
      showAlert({
        type: "error",
        message: resp?.data?.error || resp?.data?.message || "Có lỗi xảy ra",
      });
    }
  };

  const getReplies = async () => {
    const query: { [key: string]: any } = {
      parent: data?._id,
    };
    const resp = await asyncGetList({
      apiCode: "comment2",
      token,
      condition: {
        page: 1,
        limit: LIMIT,
        q: query,
      },
    });
    if (resp && resp?.status === 200) {
      const newMessages: any[] = resp?.data || [];
      newMessages.reverse();
      setMessages((prev) => [...newMessages, ...prev]);
    }
  };

  useEffect(() => {
    if (open) {
      getReplies();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  return (
    <>
      {open && (
        <ModalBase
          title="Phản hồi"
          open={open}
          handleClose={handleClose}
          width="600px"
          actions={[
            <Stack
              key={1}
              direction="row"
              gap={1}
              sx={{ width: "100%", position: "relative" }}
            >
              {isArray(files) && files?.length > 0 && (
                <Stack
                  gap={0.5}
                  sx={{
                    position: "absolute",
                    zIndex: 10,
                    left: 0,
                    bottom: "calc(100% + 8px)",
                    width: "50%",
                  }}
                >
                  {files.map((file, index) => {
                    return (
                      <Stack
                        key={v4()}
                        direction="row"
                        alignItems="center"
                        sx={{
                          backgroundColor: "background.default",
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          border: "1px solid",
                          borderColor: "divider",
                        }}
                      >
                        <Stack sx={{ flex: 1 }}>
                          <Typography
                            className="one-line-ellipsis"
                            sx={{ fontWeight: 500 }}
                          >
                            {file.name}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: "12px",
                              lineHeight: "12px",
                              fontStyle: "italic",
                            }}
                          >
                            {(file.size / 1024).toFixed(1)} KB
                          </Typography>
                        </Stack>
                        <IconButton onClick={() => deleteFile(index)}>
                          <LuXCircle size={16} />
                        </IconButton>
                      </Stack>
                    );
                  })}
                </Stack>
              )}
              <input
                key={v4()}
                type="file"
                id="attach"
                hidden
                multiple
                onChange={handleFileChange}
              />
              <TextField
                value={text}
                onChange={(e) => setText(e.target.value)}
                fullWidth
                autoComplete="off"
                placeholder="Nhập phản hồi"
                onKeyUp={(e) => {
                  if (e.which === 13) {
                    sendMessage();
                  }
                }}
                sx={{
                  "& .MuiInputBase-root": {
                    "& fieldset": {
                      border: "none",
                      outline: "none",
                    },
                  },
                }}
              />
              <Stack direction="row" gap={0.5} alignItems="center">
                <Badge badgeContent={files?.length} color="primary">
                  <IconButton
                    component="label"
                    htmlFor="attach"
                    sx={{ backgroundColor: "background.default" }}
                  >
                    <MdAttachFile size={14} />
                  </IconButton>
                </Badge>
                <IconButton
                  sx={{ color: "primary.main" }}
                  onClick={sendMessage}
                >
                  <MdSend size={20} />
                </IconButton>
              </Stack>
            </Stack>,
          ]}
        >
          <>
            {(isArray(messages) && messages?.length > 0) ||
            (isArray(moreMessages) && moreMessages?.length > 0) ? (
              <Stack gap={0.5} sx={{ height: "70vh", overflow: "auto", px: 1 }}>
                {isArray(messages) && messages?.length > 0 && (
                  <>
                    {messages.map((message) => {
                      return (
                        <Message
                          key={message?._id}
                          data={message}
                          isOwner={message?.exfields?.admin}
                        />
                      );
                    })}
                  </>
                )}
                {isArray(moreMessages) && moreMessages?.length > 0 && (
                  <>
                    {moreMessages.map((message) => {
                      return (
                        <Message
                          key={message?._id}
                          data={message}
                          isOwner={message?.exfields?.admin}
                        />
                      );
                    })}
                  </>
                )}
              </Stack>
            ) : (
              <Typography sx={{ py: 1, textAlign: "center" }}>
                Chưa có phản hồi
              </Typography>
            )}
          </>
        </ModalBase>
      )}
      <Button
        onClick={() => setOpen(true)}
        variant="contained"
        size="small"
        color="secondary"
        sx={{
          boxShadow: "none",
          textTransform: "none",
          color: "common.white",
          "&:hover": {
            boxShadow: "none",
          },
        }}
      >
        Phản hồi
      </Button>
    </>
  );
}

export default ModalReplyComplaint;

function Message({ data, isOwner }: { data: any; isOwner?: boolean }) {
  const { token } = useSelector((state: any) => state.auth);
  const { showAlert } = useAlertContext();
  const [isDeleted, setIsdeleted] = useState<boolean>(false);

  const handleDelete = async () => {
    const resp = await asyncDelete({
      apiCode: "comment2",
      id: data?._id,
      token,
    });
    if (resp && resp?.status === 200) {
      setIsdeleted(true);
    } else {
      showAlert({
        type: "error",
        message: resp?.data?.error || resp?.data?.message || "Có lỗi xảy ra",
      });
    }
  };

  if (isDeleted) {
    return null;
  }

  return (
    <Stack
      sx={{
        width: "fit-content",
        maxWidth: "70%",
        alignSelf: isOwner ? "flex-end" : "flex-start",
        position: "relative",
        "&:hover .option": {
          display: "flex",
        },
      }}
    >
      {/* Options */}
      {isOwner && (
        <Stack
          className="option"
          direction="row"
          alignItems="center"
          gap={0.5}
          sx={{
            display: "none",
            position: "absolute",
            zIndex: 10,
            top: "50%",
            left: 0,
            pr: 1,
            transform: "translate(-100%, -50%)",
          }}
        >
          <IconButton
            onClick={handleDelete}
            sx={{ backgroundColor: "background.default" }}
          >
            <LuTrash size={12} />
          </IconButton>
        </Stack>
      )}
      {/* Optons end */}
      {data?.content && (
        <Stack
          sx={{
            px: 1,
            py: 0.5,
            backgroundColor: isOwner ? "secondary.main" : "background.default",
            borderRadius: 1,
            "&:hover .option": {
              display: "flex",
            },
          }}
        >
          <Typography sx={{ color: isOwner ? "common.white" : "" }}>
            {data?.content}
          </Typography>
        </Stack>
      )}
      {isArray(data?.attends) && data?.attends?.length > 0 && (
        <Stack mt={0.5} gap={0.5}>
          {data?.attends?.map((file: any) => {
            return (
              <Stack
                key={v4()}
                direction="row"
                alignItems="center"
                sx={{
                  minWidth: "200px",
                  backgroundColor: "background.default",
                  px: 1,
                  py: 0.5,
                  borderRadius: 1,
                  border: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Stack sx={{ flex: 1 }}>
                  <Typography
                    className="one-line-ellipsis"
                    sx={{ fontWeight: 500 }}
                  >
                    Tệp đính kèm
                  </Typography>
                </Stack>
                <IconButton
                  onClick={() =>
                    downloadFile({
                      link: file?.url,
                      successMessage: "Đã tải về đính kèm",
                    })
                  }
                >
                  <FiDownload size={16} />
                </IconButton>
              </Stack>
            );
          })}
        </Stack>
      )}
      <Stack
        direction="row"
        alignItems="center"
        mt={0.5}
        gap={2}
        sx={{ justifyContent: "space-between" }}
      >
        <Typography
          sx={{
            fontSize: "10px",
            lineHeight: "10px",
            color: "text.secondary",
          }}
        >
          {formatDateDisplay(data?.date_created, "HH:mm DD/MM/YYYY")}
        </Typography>
        {isOwner ? (
          <Typography
            sx={{
              fontSize: "10px",
              lineHeight: "10px",
              color: "text.secondary",
            }}
          >
            {data?.name_user_created}
          </Typography>
        ) : (
          <div></div>
        )}
      </Stack>
    </Stack>
  );
}
